import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  ChevronRight,
  Plus,
  Video,
  Sparkles,
  ClipboardList,
  Lock,
  Brain,
  Flame,
  Star,
  Heart,
  ArrowRight,
  FileText,
  MessageCircle,
  Smile,
  Crown,
  X,
  Gift,
  Zap,
  UserRound,
  CalendarDays,
  Check,
} from 'lucide-react';
import VerificationBanner from './VerificationBanner';
import NearestSlotCard from './NearestSlotCard';
import MoodMonitor from './MoodMonitor';
import { FaWhatsapp } from 'react-icons/fa';
import { fetchMembershipPricing } from '../services/FirebaseService';
import { doctorService } from '../services/doctorService';

// Anillos de color para los avatares de doctoras (paleta de especialidades del landing).
const AVATAR_RINGS = ['#FF8A65', '#786ED9', '#52A87C', '#3D3580', '#F5C842'];
const WA_HELP_URL = 'https://api.whatsapp.com/send/?phone=5217778012777&text=Hola%2C+me+gustar%C3%ADa+apoyo+para+agendar+una+consulta.&type=phone_number&app_absent=0';

/* ═══════════════════════════════════════════
   Circular Progress Ring (SVG)
   ═══════════════════════════════════════════ */
const RingProgress = ({ remaining, total }) => {
  const radius = 40;
  const circumference = 2 * Math.PI * radius; // ~251
  const pct = Math.max(0, Math.min(1, remaining / total));
  const offset = circumference * (1 - pct);

  return (
    <svg width="100" height="100" viewBox="0 0 100 100" className="drop-shadow-sm">
      {/* Background track */}
      <circle
        cx="50" cy="50" r={radius}
        fill="none"
        stroke="rgba(255,255,255,0.2)"
        strokeWidth="8"
        strokeLinecap="round"
      />
      {/* Progress arc */}
      <circle
        cx="50" cy="50" r={radius}
        fill="none"
        stroke="white"
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        style={{
          transform: 'rotate(-90deg)',
          transformOrigin: '50% 50%',
          transition: 'stroke-dashoffset 1.2s cubic-bezier(0.16,1,0.3,1)',
        }}
      />
      {/* Clock icon in center */}
      <g transform="translate(38, 38)">
        <Clock size={24} color="white" strokeWidth={2} />
      </g>
    </svg>
  );
};

/* ═══════════════════════════════════════════
   Main Dashboard Component
   ═══════════════════════════════════════════ */
const LoggedInDashboard = ({
  user,
  reservation,
  consultations = [],
  onStartBooking,
  onLogout,
  onStartVerification,
  onSelectMembership,
  onRouteStartBooking,
}) => {
  const firstName = user?.nombre || user?.name || 'Usuario';
  const [detailConsultation, setDetailConsultation] = useState(null);
  const [showMembershipPopup, setShowMembershipPopup] = useState(false);

  // Precio mensual real de la Membresía Integral (id 21) desde el backend
  // (Firestore dais_membership.monthly_price). Fallback a 399 si no carga.
  const [membershipMonthly, setMembershipMonthly] = useState(399);
  useEffect(() => {
    let cancelled = false;
    fetchMembershipPricing([21])
      .then((map) => {
        const m = map?.[21]?.monthlyPrice;
        if (!cancelled && Number.isFinite(m) && m > 0) setMembershipMonthly(m);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // ── Carousel de promo (solo no-miembros): membresía / consulta / test ──
  const [promoSlide, setPromoSlide] = useState(0);
  const PROMO_COUNT = 3;
  useEffect(() => {
    if (user?.membership_active) return;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;
    const id = setInterval(() => setPromoSlide((s) => (s + 1) % PROMO_COUNT), 5000);
    return () => clearInterval(id);
  }, [user?.membership_active]);

  // ── Avatares reales de doctoras para la acción "Agendar consulta" (presentación) ──
  const [docAvatars, setDocAvatars] = useState([]);
  useEffect(() => {
    let cancelled = false;
    doctorService.getDoctorsBySpecialty(16)
      .then((list) => {
        if (cancelled || !Array.isArray(list)) return;
        setDocAvatars(list.filter((d) => d?.avatar).slice(0, 5).map((d) => d.avatar));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Diapositivas del carousel — colores alineados con secciones del landing.
  // La slide de consulta es foto-driven (foto blurred + scrim) para diferenciarla.
  const promoSlides = [
    {
      key: 'membership',
      bg: 'bg-brand', fg: 'text-white', eyebrow: 'text-white/70', sub: 'text-white/80',
      photo: '/dais-girls-gen/chikichiki.png', photoMode: 'right',
      Icon: Crown,
      label: 'Membresía Daisies',
      title: 'Obtén tu membresía',
      desc: `$${membershipMonthly} MXN/mes · 70 min · todas las especialidades`,
      cta: 'Comenzar ahora',
      ctaCls: 'bg-white text-brand-dark hover:bg-brand-tint',
      onClick: () => onSelectMembership?.({ name: 'Membresía Integral', price: String(membershipMonthly), membershipId: 21 }),
    },
    {
      key: 'consulta',
      bg: 'bg-brand-deep', fg: 'text-white', eyebrow: 'text-white/70', sub: 'text-white/85',
      photo: '/dais-girls-gen/chikicafe.png', photoMode: 'cover',
      scrim: 'bg-gradient-to-r from-brand-deep via-brand-deep/70 to-brand-deep/20',
      Icon: CalendarDays,
      label: 'Tu primera consulta',
      title: 'Agenda tu primera consulta',
      desc: 'Elige especialidad, mira horarios reales y aparta tu lugar en minutos.',
      cta: 'Agendar ahora',
      ctaCls: 'bg-white text-brand-deep hover:bg-brand-tint',
      onClick: () => onStartBooking(),
    },
    {
      key: 'test',
      bg: 'bg-amber-pill', fg: 'text-amber-deep', eyebrow: 'text-amber-deep/70', sub: 'text-amber-deep/80',
      Icon: ClipboardList,
      label: 'Test gratis',
      title: '¿No sabes por dónde empezar?',
      desc: 'Un test de 2 minutos te orienta sobre qué necesitas.',
      cta: 'Hacer test gratis',
      ctaCls: 'bg-amber-deep text-white hover:bg-ink',
      onClick: () => window.open('https://daisies.mx/tests/perfil-integral', '_blank'),
    },
  ];

  // Show membership popup after delay for non-members
  useEffect(() => {
    if (user?.membership_active) return;
    const alreadyShown = sessionStorage.getItem('daisies_membership_popup_shown');
    if (alreadyShown) return;
    const timer = setTimeout(() => {
      setShowMembershipPopup(true);
      sessionStorage.setItem('daisies_membership_popup_shown', '1');
    }, 5000);
    return () => clearTimeout(timer);
  }, [user?.membership_active]);

  // ── Extract upcoming consultations ──
  const extractDate = (consultation) => {
    const raw = consultation?.calendar_event?.start || '';
    if (!raw) return '';
    return raw.split(' ')[0] || '';
  };

  const today = new Date().toISOString().split('T')[0];
  const upcomingConsultations = consultations
    .filter((c) => {
      const dateStr = extractDate(c);
      if (!dateStr) return true;
      return dateStr >= today;
    })
    .sort((a, b) => {
      const dA = extractDate(a);
      const dB = extractDate(b);
      return dA.localeCompare(dB);
    });

  const nextConsultation = upcomingConsultations[0] || null;

  // ── Remaining minutes logic (from Firebase dais_user.balance) ──
  const remainingMinutes = user?.balance ?? 0;
  const TOTAL_MINUTES = 70; // Used only for the ring progress visual

  // ── Build detail data for any consultation ──
  const buildConsultationDetail = (consultation) => {
    if (!consultation) return null;
    const calendarEvent = consultation.calendar_event || {};
    const professional = consultation.professional || {};
    const dateStr = extractDate(consultation);
    let dateDisplay = 'Por confirmar';
    let dayNumber = '';
    let monthShort = '';
    if (dateStr) {
      try {
        const d = new Date(dateStr + 'T12:00:00');
        dayNumber = d.getDate();
        monthShort = d
          .toLocaleDateString('es-MX', { month: 'short' })
          .toUpperCase()
          .replace('.', '');
        dateDisplay = d.toLocaleDateString('es-MX', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        });
      } catch { /* keep default */ }
    }
    return {
      specialty: consultation.specialty?.name || 'Consulta',
      doctorName: professional.name
        ? `${professional.name} ${professional.last_name || ''}`.trim()
        : 'Especialista',
      doctorAvatar: professional.avatar || '',
      time: calendarEvent.hour_start || '',
      timeEnd: calendarEvent.hour_stop || '',
      dateDisplay,
      dayNumber,
      monthShort,
      duration: calendarEvent.duration || consultation.duration || 30,
      state: consultation.state || '',
      isExtra: consultation.is_extra || false,
      reason: consultation.reason || '',
      videocallLocation: calendarEvent.videocall_location || '',
    };
  };

  // ── Next consultation display data ──
  const nextDoc = nextConsultation ? buildConsultationDetail(nextConsultation) : null;

  // ── Journey badges ──
  const badges = [
    {
      icon: <Heart size={18} />,
      label: 'Primera Consulta',
      color: 'bg-rose-100 text-rose-600',
      earned: consultations.length > 0,
    },
    {
      icon: <Smile size={18} />,
      label: 'Primer test de salud',
      color: 'bg-emerald-100 text-emerald-600',
      earned: consultations.length >= 3,
    },
    {
      icon: <Star size={18} />,
      label: '¡Expediente Médico Completo!',
      color: 'bg-amber-100 text-amber-600',
      earned: false,
    },
    {
      icon: <Flame size={18} />,
      label: 'Super Saver',
      color: 'bg-blue-100 text-blue-600',
      earned: false,
    },
  ];

  // Color por test = colores SÓLIDOS canónicos del landing (lib/tests-data.ts → colorThemes.bg):
  // ansiedad=coral, burnout=amber, bienestar=mint, perfil-integral=violet.
  // card = color sólido; chip = tono translúcido del mismo color (sin burbuja blanca); text contrastante.
  const emotionalTests = [
    { label: 'Ansiedad', Icon: Brain, url: 'https://daisies.mx/tests/ansiedad',
      card: 'bg-[#FF8A65]', chip: 'bg-white/20', iconColor: '#ffffff', text: 'text-white' },
    { label: 'Burnout', Icon: Flame, url: 'https://daisies.mx/tests/burnout',
      card: 'bg-amber-pill', chip: 'bg-[#5a4400]/15', iconColor: '#5a4400', text: 'text-[#5a4400]' },
    { label: 'Bienestar', Icon: Heart, url: 'https://daisies.mx/tests/bienestar',
      card: 'bg-mint-pill', chip: 'bg-mint-deep/15', iconColor: '#065F46', text: 'text-mint-deep' },
    { label: 'Todos', Icon: ClipboardList, url: 'https://daisies.mx/tests/perfil-integral',
      card: 'bg-brand', chip: 'bg-white/20', iconColor: '#ffffff', text: 'text-white' },
  ];

  // Sección "Tienda Daisies" deshabilitada temporalmente:
  // los items eran placeholders sin links reales y los precios estaban incorrectos.
  // Reactivar cuando exista un catálogo real conectado a backend.

  return (
    <div className="w-full mb-10 space-y-5">
      {/* ═══ 1. REMAINING TIME CARD or MEMBERSHIP CTA ═══ */}
      {!user?.membership_active ? (
        /* ── No membership: carousel (membresía / consulta / test) ── */
        <div className="animate-fadeInUp relative overflow-hidden rounded-card" role="region" aria-label="Promociones Daisies">
          <div
            className="flex items-stretch transition-transform duration-500 ease-out-quart"
            style={{ transform: `translateX(-${promoSlide * 100}%)` }}
          >
            {promoSlides.map((s, i) => (
              <button
                key={s.key}
                type="button"
                onClick={s.onClick}
                aria-hidden={promoSlide !== i}
                tabIndex={promoSlide === i ? 0 : -1}
                aria-label={s.title}
                className="press-scale-card w-full shrink-0 text-left cursor-pointer"
              >
                <div className={`${s.bg} ${s.fg} w-full rounded-card p-6 pb-12 relative overflow-hidden min-h-[210px] flex items-center justify-between gap-4`}>
                  {s.photoMode === 'cover' ? (
                    <>
                      <img src={s.photo} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-top blur-[3px] scale-110" style={{ opacity: 0.6 }} />
                      <div aria-hidden="true" className={`absolute inset-0 ${s.scrim}`} />
                    </>
                  ) : s.photoMode === 'right' ? (
                    <img
                      src={s.photo}
                      alt=""
                      aria-hidden="true"
                      className="absolute right-0 top-0 h-full w-[46%] sm:w-[52%] object-cover object-top pointer-events-none"
                      style={{ opacity: 0.6, maskImage: 'linear-gradient(to right, transparent 0%, #000 55%)', WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 55%)' }}
                    />
                  ) : (
                    <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" aria-hidden="true" />
                  )}
                  <div className="relative z-10 flex-1">
                    <p className={`${s.eyebrow} text-[11px] font-bold mb-1.5 tracking-[0.1em] uppercase`}>{s.label}</p>
                    <h2 className="text-[1.6rem] font-bold leading-[1.1] tracking-[-0.02em] mb-1.5 text-balance">{s.title}</h2>
                    <p className={`${s.sub} text-[13px] mb-4 max-w-[16rem] leading-snug`}>{s.desc}</p>
                    <span className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${s.ctaCls}`}>
                      <s.Icon size={16} aria-hidden="true" />
                      {s.cta}
                    </span>
                  </div>
                  {!s.photo && (
                    <div className="relative z-10 flex-shrink-0 hidden sm:flex">
                      <span className="w-[92px] h-[92px] bg-white/15 rounded-full flex items-center justify-center">
                        <s.Icon size={38} aria-hidden="true" />
                      </span>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
          {/* Dots */}
          <div className="absolute bottom-4 left-6 z-20 flex gap-1.5">
            {promoSlides.map((s, i) => (
              <button
                key={s.key}
                onClick={() => setPromoSlide(i)}
                aria-label={`Ir a ${s.label}`}
                aria-current={promoSlide === i}
                className={`h-1.5 rounded-full transition-all ${promoSlide === i ? 'w-5 bg-white' : 'w-1.5 bg-white/45 hover:bg-white/70'}`}
              />
            ))}
          </div>
        </div>
      ) : (
        /* ── Has membership: show remaining time ── */
        <div className="animate-fadeInUp bg-brand rounded-card p-6 text-white relative overflow-hidden">
          {/* Decorative blurred circle */}
          <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full blur-xl" />

          <div className="relative z-10 flex items-center justify-between">
            <div className="flex-1">
              <p className="text-white/70 text-sm font-medium mb-1 tracking-wide uppercase">
                Tiempo Restante
              </p>
              <div className="flex items-baseline gap-1.5">
                <span className="text-5xl font-bold tracking-tight leading-none">
                  {remainingMinutes}
                </span>
                <span className="text-xl font-medium text-white/80">min</span>
              </div>
              {/* <p className="text-white/60 text-xs mt-2">Se reinicia en 30 días</p> */}
              <button
                onClick={() => onStartBooking()}
                className="press-scale mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-sm font-semibold transition-all"
              >
                <Plus size={16} />
                Agendar consulta
              </button>
            </div>

            <div className="flex-shrink-0 ml-4">
              <RingProgress remaining={remainingMinutes} total={TOTAL_MINUTES} />
            </div>
          </div>
        </div>
      )}

      {/* ═══ Verification Banner ═══ */}
      <VerificationBanner
        stepState={user?.stepState}
        phoneVerified={user?.phoneVerified}
        onStartVerification={onStartVerification}
      />

      {/* ═══ NEXT CONSULTATION (arriba: es lo más importante cuando existe;
           el empty state "Agenda tu próxima consulta" se queda abajo) ═══ */}
      {nextDoc && (
        <div className="animate-fadeInUp-1">
          <h3 className="text-base font-semibold text-text-primary mb-3">Próxima Cita</h3>
          <div className="bg-white rounded-card shadow-soft overflow-hidden">
            <div className="p-5">
              {/* Doctor row */}
              <div className="flex items-center gap-3 mb-4">
                {nextDoc.doctorAvatar ? (
                  <img
                    src={nextDoc.doctorAvatar}
                    alt={nextDoc.doctorName}
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-daisies-50"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-brand-tint flex items-center justify-center">
                    <UserRound className="text-brand" size={20} aria-hidden="true" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-text-primary truncate">
                    {nextDoc.doctorName}
                  </p>
                  <p className="text-sm text-text-secondary truncate">{nextDoc.specialty}</p>
                </div>
              </div>

              {/* Meta row */}
              <div className="flex items-center gap-3 text-sm text-text-secondary mb-4">
                <span className="inline-flex items-center gap-1">
                  <Video size={14} className="text-daisies" />
                  Video
                </span>
                <span className="text-text-secondary/40">·</span>
                <span>{nextDoc.duration} min</span>
              </div>

              {/* Date + Details row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="bg-surface rounded-input px-3 py-2 text-center min-w-[52px]">
                    <p className="text-lg font-bold text-text-primary leading-none">
                      {nextDoc.dayNumber}
                    </p>
                    <p className="text-[10px] font-semibold text-text-secondary uppercase mt-0.5">
                      {nextDoc.monthShort}
                    </p>
                  </div>
                  {nextDoc.time && (
                    <span className="text-sm text-text-secondary font-medium">
                      {nextDoc.time}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setDetailConsultation(nextConsultation)}
                  className="press-scale px-4 py-2 border-2 border-gray-200 rounded-full text-sm font-semibold text-text-primary hover:border-daisies hover:text-daisies transition-all"
                >
                  Detalles
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ACCIONES RÁPIDAS (arriba de Tu Recorrido) ═══ */}
      <div className="animate-fadeInUp-1">
        <h3 className="text-base font-semibold text-text-primary mb-3">Acciones rápidas</h3>
        <button
          onClick={() => onStartBooking()}
          className="press-scale-card w-full bg-surface-card rounded-card p-6 text-left transition-transform flex items-center justify-between gap-4 mb-3"
        >
          <div>
            <span className="flex items-center justify-center w-11 h-11 rounded-2xl bg-brand-tint text-brand mb-3">
              <Calendar size={22} aria-hidden="true" />
            </span>
            <p className="font-bold text-brand-deep text-lg leading-tight">Agendar consulta</p>
            <p className="text-text-mute text-sm mt-1">Doctoras con horarios disponibles hoy</p>
          </div>
          <div className="flex -space-x-3 shrink-0" aria-hidden="true">
            {(docAvatars.length ? docAvatars : [null, null, null, null]).slice(0, 5).map((a, i) => (
              <span
                key={i}
                className="w-11 h-11 rounded-full ring-2 ring-surface-card overflow-hidden flex items-center justify-center"
                style={{ background: AVATAR_RINGS[i % AVATAR_RINGS.length] }}
              >
                {a && <img src={a} alt="" className="w-full h-full object-cover" />}
              </span>
            ))}
            <span className="w-11 h-11 rounded-full ring-2 ring-surface-card bg-brand flex items-center justify-center">
              <Check size={18} className="text-white" aria-hidden="true" />
            </span>
          </div>
        </button>

        {/* Slot garantizado más cercano (Psicología) — clickeable, dispara gates */}
        <div className="mb-3">
          <NearestSlotCard
            onStartBooking={onRouteStartBooking || onStartBooking}
            userId={user?.daisUserId || user?.userId}
            authToken={user?.authToken}
          />
        </div>

        {/* ── MoodMonitor (monitoreo de salud) ── */}
        <div className="mb-3">
          <MoodMonitor
            userId={user?.daisUserId || user?.userId}
            authToken={user?.authToken}
            tz="America/Mexico_City"
          />
        </div>

        <button
          onClick={() => window.open(WA_HELP_URL, '_blank')}
          className="press-scale-card w-full bg-surface-card rounded-card p-5 transition-transform text-left flex items-center gap-3"
        >
          <span className="flex items-center justify-center w-11 h-11 rounded-2xl bg-mint-pill text-mint-deep shrink-0">
            <FaWhatsapp size={20} />
          </span>
          <p className="font-semibold text-brand-deep text-sm">Hacer una pregunta por WhatsApp</p>
        </button>
      </div>

      {/* ═══ 2. YOUR JOURNEY ═══ */}
      <div className="animate-fadeInUp-1">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-text-primary">Tu Recorrido</h3>
          <a
            href="https://checkup.daisies.mx"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold text-daisies uppercase tracking-wider hover:text-daisies-hover transition-colors"
          >
            Ver Todo
          </a>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none">
          {badges.map((badge, i) => (
            <div
              key={i}
              className={`flex-shrink-0 flex flex-col items-center gap-1.5 w-20 ${
                !badge.earned ? 'opacity-50' : ''
              }`}
            >
              <div
                className={`relative w-14 h-14 rounded-2xl flex items-center justify-center shadow-soft transition-transform hover:scale-105 ${
                  badge.earned
                    ? badge.color
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {badge.icon}
                {!badge.earned && (
                  <div className="absolute -bottom-0.5 -right-0.5 bg-white rounded-full p-0.5 shadow-sm">
                    <Lock size={10} className="text-gray-400" />
                  </div>
                )}
              </div>
              <span className="text-[10px] font-medium text-text-secondary text-center leading-tight">
                {badge.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ═══ 5. EMOTIONAL TESTS ═══ */}
      <div className="animate-fadeInUp-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-text-primary flex items-center gap-2">
            <Brain size={18} className="text-daisies" />
            Tests Emocionales
          </h3>
        </div>
        <div className="grid grid-cols-4 gap-2.5">
          {emotionalTests.map((test, i) => (
            <a
              key={i}
              href={test.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`press-scale-card ${test.card} rounded-2xl p-3 text-center transition-transform group`}
            >
              <span className={`flex items-center justify-center mx-auto mb-1.5 w-9 h-9 rounded-full ${test.chip} group-hover:scale-110 transition-transform`} style={{ color: test.iconColor }}>
                <test.Icon size={18} aria-hidden="true" />
              </span>
              <span className={`text-[11px] font-semibold leading-tight ${test.text}`}>
                {test.label}
              </span>
            </a>
          ))}
        </div>
      </div>

      {/* ═══ 6. TIENDA DAISIES (oculta temporalmente — links rotos / precios mal) ═══ */}

      {/* ═══ 7. COMPLETAR EXPEDIENTE ═══ */}
      <div className="animate-fadeInUp-6">
        <div
          onClick={async () => {
            try {
              const { auth } = await import('../services/FirebaseService');
              const fbUser = auth.currentUser;
              if (fbUser) {
                const jwt = await fbUser.getIdToken(true);
                window.open(
                  `https://app.daisies.mx/authPage?jwt=${encodeURIComponent(jwt)}`,
                  '_blank'
                );
              } else {
                window.open('https://app.daisies.mx', '_blank');
              }
            } catch {
              window.open('https://app.daisies.mx', '_blank');
            }
          }}
          className="press-scale cursor-pointer bg-amber-pill/20 rounded-card p-5 flex items-center gap-4 transition-colors group"
        >
          <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
            <FileText size={22} className="text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-text-primary text-sm">
              Completa tu Expediente Médico
            </p>
            <p className="text-xs text-text-secondary mt-0.5">
              Mejora la atención que recibes con info completa
            </p>
          </div>
          <ChevronRight
            size={20}
            className="text-amber-400 flex-shrink-0 group-hover:translate-x-1 transition-transform"
          />
        </div>
      </div>

      {/* ═══ Additional consultations (if more than 1 upcoming) ═══ */}
      {upcomingConsultations.length > 1 && (
        <div className="animate-fadeInUp-6">
          <h3 className="text-base font-semibold text-text-primary mb-3">
            Otras Citas Agendadas
          </h3>
          <div className="space-y-2.5">
            {upcomingConsultations.slice(1, 4).map((consultation, idx) => {
              const calEvent = consultation.calendar_event || {};
              const prof = consultation.professional || {};
              const specName = consultation.specialty?.name || 'Consulta';
              const docName = prof.name
                ? `${prof.name} ${prof.last_name || ''}`.trim()
                : '';
              const dateStr = extractDate(consultation);
              let dateLabel = '';
              if (dateStr) {
                try {
                  dateLabel = new Date(dateStr + 'T12:00:00').toLocaleDateString(
                    'es-MX',
                    { day: 'numeric', month: 'short' }
                  );
                } catch {
                  /* keep empty */
                }
              }

              return (
                <div
                  key={consultation.firestoreId || idx}
                  className="bg-white rounded-2xl shadow-soft p-4 flex items-center gap-3"
                >
                  {prof.avatar ? (
                    <img
                      src={prof.avatar}
                      alt={docName}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-brand-tint flex items-center justify-center">
                      <UserRound className="text-brand" size={18} aria-hidden="true" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-primary text-sm truncate">
                      {docName || specName}
                    </p>
                    <p className="text-xs text-text-secondary">
                      {dateLabel}
                      {calEvent.hour_start ? ` · ${calEvent.hour_start}` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => setDetailConsultation(consultation)}
                    className="press-scale text-xs font-medium text-daisies bg-daisies-50 px-2.5 py-1 rounded-full hover:bg-daisies-100 transition-colors"
                  >
                    Detalles
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ No consultations — Booking CTA ═══ */}
      {!nextDoc && !reservation && (
        <div className="animate-fadeInUp-2">
          <div className="bg-surface-card rounded-card p-6 text-center">
            <span className="flex items-center justify-center mx-auto mb-3 w-12 h-12 rounded-full bg-brand-tint text-brand">
              <CalendarDays size={24} aria-hidden="true" />
            </span>
            <h3 className="font-semibold text-text-primary text-lg mb-1">
              Agenda tu próxima consulta
            </h3>
            <p className="text-text-secondary text-sm mb-5">
              Tienes {remainingMinutes} minutos disponibles para tu bienestar
            </p>
            <button
              onClick={() => onStartBooking()}
              className="press-scale group px-8 py-3.5 bg-daisies text-white rounded-full font-semibold text-base hover:bg-daisies-hover transition-all shadow-soft hover:shadow-elevated inline-flex items-center gap-2"
            >
              Agendar consulta
              <ChevronRight
                className="group-hover:translate-x-1 transition-transform"
                size={18}
              />
            </button>
          </div>
        </div>
      )}

      {/* ═══ DETAILS MODAL ═══ */}
      {detailConsultation && (() => {
        const detail = buildConsultationDetail(detailConsultation);
        if (!detail) return null;
        return (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center animate-fadeIn"
            onClick={() => setDetailConsultation(null)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

            {/* Modal card */}
            <div
              className="relative bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-elevated p-6 pb-8 max-h-[85vh] overflow-y-auto animate-slideUp"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-text-primary">Detalles de la Cita</h3>
                <button
                  onClick={() => setDetailConsultation(null)}
                  className="w-8 h-8 rounded-full bg-surface flex items-center justify-center hover:bg-gray-200 transition-colors"
                >
                  <X size={18} className="text-text-secondary" />
                </button>
              </div>

              {/* Doctor info */}
              <div className="flex items-center gap-4 mb-5">
                {detail.doctorAvatar ? (
                  <img
                    src={detail.doctorAvatar}
                    alt={detail.doctorName}
                    className="w-16 h-16 rounded-2xl object-cover ring-2 ring-daisies-50 shadow-soft"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-brand-tint flex items-center justify-center">
                    <UserRound className="text-brand" size={28} aria-hidden="true" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-text-primary text-base truncate">
                    {detail.doctorName}
                  </p>
                  <p className="text-sm text-daisies font-medium">{detail.specialty}</p>
                </div>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                {/* Date */}
                <div className="bg-surface rounded-2xl p-3.5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Calendar size={14} className="text-daisies" />
                    <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">Fecha</span>
                  </div>
                  <p className="text-sm font-semibold text-text-primary capitalize">{detail.dateDisplay}</p>
                </div>

                {/* Time */}
                <div className="bg-surface rounded-2xl p-3.5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Clock size={14} className="text-daisies" />
                    <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">Hora</span>
                  </div>
                  <p className="text-sm font-semibold text-text-primary">
                    {detail.time || 'Por confirmar'}
                    {detail.timeEnd ? ` – ${detail.timeEnd}` : ''}
                  </p>
                </div>

                {/* Duration */}
                <div className="bg-surface rounded-2xl p-3.5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Video size={14} className="text-daisies" />
                    <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">Duración</span>
                  </div>
                  <p className="text-sm font-semibold text-text-primary">{detail.duration} min</p>
                </div>

                {/* Type */}
                <div className="bg-surface rounded-2xl p-3.5">
                  <div className="flex items-center gap-2 mb-1.5">
                    {detail.isExtra ? (
                      <Sparkles size={14} className="text-amber-500" />
                    ) : (
                      <Crown size={14} className="text-daisies" />
                    )}
                    <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">Tipo</span>
                  </div>
                  <p className={`text-sm font-semibold ${
                    detail.isExtra ? 'text-amber-600' : 'text-daisies'
                  }`}>
                    {detail.isExtra ? 'Consulta Extra' : 'Consulta Incluida'}
                  </p>
                </div>
              </div>

              {/* Reason (if present) */}
              {detail.reason && (
                <div className="bg-surface rounded-2xl p-3.5 mb-5">
                  <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide mb-1.5">Motivo</p>
                  <p className="text-sm text-text-primary">{detail.reason}</p>
                </div>
              )}

              {/* State badge */}
              <div className="flex items-center justify-between mb-5">
                <span className="text-sm text-text-secondary">Estado</span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  detail.state === 'scheduled'
                    ? 'bg-emerald-50 text-emerald-600'
                    : detail.state === 'draft'
                    ? 'bg-amber-50 text-amber-600'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {detail.state === 'scheduled' ? 'Confirmada'
                    : detail.state === 'draft' ? 'Pendiente'
                    : detail.state || 'Pendiente'}
                </span>
              </div>

              {/* Google Meet button */}
              {detail.videocallLocation && (
                <a
                  href={detail.videocallLocation}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="press-scale w-full py-3.5 mb-3 bg-[#00897B] hover:bg-[#00796B] text-white rounded-full font-semibold text-sm transition-all shadow-soft flex items-center justify-center gap-2.5"
                >
                  {/* Google Meet icon */}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="white" fillOpacity="0.15"/>
                    <path d="M14.5 8.5v2.2l3-2.7v8l-3-2.7V15.5c0 .55-.45 1-1 1h-5c-.55 0-1-.45-1-1v-6c0-.55.45-1 1-1h5c.55 0 1 .45 1 1z" fill="white"/>
                  </svg>
                  Unirme a Consulta
                </a>
              )}

              {/* Close button */}
              <button
                onClick={() => setDetailConsultation(null)}
                className="w-full py-3 bg-surface text-text-secondary rounded-full font-semibold text-sm hover:bg-gray-200 transition-all"
              >
                Cerrar
              </button>
            </div>
          </div>
        );
      })()}

      {/* ═══ MEMBERSHIP PROMO POPUP (Temu-style) ═══ */}
      {showMembershipPopup && !user?.membership_active && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setShowMembershipPopup(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          <div
            className="relative w-full max-w-sm overflow-hidden rounded-3xl shadow-2xl animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Inline keyframes for popup animations */}
            <style>{`
              @keyframes popupShine {
                0% { transform: translateX(-100%) rotate(25deg); }
                100% { transform: translateX(200%) rotate(25deg); }
              }
              @keyframes popupPulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.08); }
              }
              @keyframes popupFloat {
                0%, 100% { transform: translateY(0px) rotate(0deg); }
                33% { transform: translateY(-8px) rotate(5deg); }
                66% { transform: translateY(4px) rotate(-3deg); }
              }
              @keyframes popupConfetti {
                0% { opacity: 0; transform: translateY(20px) scale(0.5); }
                50% { opacity: 1; transform: translateY(-10px) scale(1.2); }
                100% { opacity: 0.8; transform: translateY(0) scale(1); }
              }
              .popup-shine::after {
                content: '';
                position: absolute;
                top: -50%;
                left: -50%;
                width: 200%;
                height: 200%;
                background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
                animation: popupShine 3s infinite;
              }
            `}</style>

            {/* Top gradient banner */}
            <div className="popup-shine relative bg-brand-deep p-6 pb-10 text-center overflow-hidden">
              {/* Acento amber sutil con radial gradient en esquina (patrón dark-hero del DS) */}
              <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 50% at 90% 100%, rgba(245,200,66,0.18) 0%, transparent 60%)' }} />
              {/* Sparkles decorativos (lucide, no emojis) */}
              <Sparkles size={18} className="absolute top-3 left-4 text-amber-pill" style={{animation: 'popupConfetti 2s ease-out forwards 0.3s', opacity: 0}} aria-hidden="true" />
              <Sparkles size={14} className="absolute top-5 right-6 text-white/80" style={{animation: 'popupConfetti 2s ease-out forwards 0.5s', opacity: 0}} aria-hidden="true" />
              <Sparkles size={13} className="absolute top-3 right-16 text-amber-pill" style={{animation: 'popupConfetti 2s ease-out forwards 0.7s', opacity: 0}} aria-hidden="true" />
              <Sparkles size={13} className="absolute bottom-12 left-8 text-white/70" style={{animation: 'popupConfetti 2s ease-out forwards 0.9s', opacity: 0}} aria-hidden="true" />
              <Sparkles size={13} className="absolute bottom-14 right-10 text-amber-pill" style={{animation: 'popupConfetti 2s ease-out forwards 1.1s', opacity: 0}} aria-hidden="true" />

              {/* Close button */}
              <button
                onClick={() => setShowMembershipPopup(false)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/40 transition-colors z-10"
              >
                <X size={16} className="text-white" />
              </button>

              {/* Badge */}
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 rounded-full text-white text-xs font-bold uppercase tracking-wider mb-3"
                style={{animation: 'popupPulse 2s ease-in-out infinite'}}
              >
                <Zap size={12} />
                Oferta Exclusiva
              </div>

              {/* Main icon */}
              <div
                className="mx-auto w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mb-3"
                style={{animation: 'popupFloat 3s ease-in-out infinite'}}
              >
                <Crown size={36} className="text-white drop-shadow-lg" />
              </div>

              <h3 className="text-white text-2xl font-extrabold leading-tight">
                ¡Membresía Daisies!
              </h3>
              <p className="text-white/80 text-sm mt-1">Tu bienestar merece lo mejor.</p>
            </div>

            {/* Body */}
            <div className="bg-white p-6 -mt-4 rounded-t-3xl relative">
              {/* Price tag */}
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-surface-card rounded-2xl shadow-elevated px-6 py-2.5 border border-brand-100">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-extrabold text-brand-deep">${membershipMonthly}</span>
                  <span className="text-sm text-text-mute font-medium">/mes</span>
                </div>
              </div>

              {/* Benefits */}
              <div className="mt-8 space-y-3 mb-6">
                {[
                  { Icon: Clock, text: '70 minutos de consultas al mes' },
                  { Icon: UserRound, text: 'Todas las especialidades incluidas' },
                  { Icon: Video, text: 'Consultas por videollamada' },
                  { Icon: Heart, text: 'Acceso a la comunidad Daisies' },
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-tint text-brand flex-shrink-0">
                      <benefit.Icon size={16} aria-hidden="true" />
                    </span>
                    <span className="text-sm text-text-strong font-medium">{benefit.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <button
                onClick={() => {
                  setShowMembershipPopup(false);
                  onSelectMembership?.({
                    name: 'Membresía Integral',
                    price: String(membershipMonthly),
                    membershipId: 21,
                  });
                }}
                className="w-full py-4 bg-gradient-to-r from-[#FF6B6B] via-[#EE5A24] to-[#F79F1F] text-white rounded-2xl font-extrabold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
                style={{animation: 'popupPulse 2.5s ease-in-out infinite'}}
              >
                <span className="flex items-center justify-center gap-2">
                  <Gift size={18} />
                  ¡Quiero mi Membresía!
                </span>
              </button>

              {/* Dismiss text */}
              <button
                onClick={() => setShowMembershipPopup(false)}
                className="w-full text-center text-xs text-gray-400 mt-3 hover:text-gray-600 transition-colors"
              >
                Quizás más tarde
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoggedInDashboard;
